import {AxiosResponse} from 'axios';
import {Announcement, NewAnnouncementData} from '../../types/announcement';
import {PaginationResponse, Response} from '../../types/api';
import {User} from '../../types/auth';
import {Company} from '../../types/company';
import {NewCompanyForm} from '../../types/newCompany';
import protectedInstance from '../instances/protected';
import publicInstance from '../instances/public';
import {Folder} from "../../views/admin/ImageUpload/ImageUpload";
import {useEffect, useRef, useState} from "react";
import {Crop, PixelCrop} from "react-image-crop";

async function getCompanies(
    text: string, page: number, pageSize: number
) {
    const query = `query=${text}&page=${page}&page_size=${pageSize}`;

    return protectedInstance
        .get(`/companies/all?${query}`)
        .then((res: AxiosResponse<PaginationResponse<Company[]>>) => res.data)
}

async function saveNewCompany(data: NewCompanyForm) {
    return protectedInstance
        .post("/company", data)
        .then((res: AxiosResponse<Response<any>>) => res.data)
}

async function getUsers(
    page?: number, pageSize?: number
) {
    const query = `page=${page ?? ""}&page_size=${pageSize ?? ""}`;

    return protectedInstance
        .get(`/users?${query}`)
        .then((res: AxiosResponse<PaginationResponse<User[]>>) => res.data)
}

async function saveNewAnnouncement(data: NewAnnouncementData) {
    return protectedInstance
        .post("/news", data)
        .then((res: AxiosResponse<Response<Announcement>>) => res.data)
}

async function getAnnouncements(
    page: number, pageSize: number
) {
    const query = `page=${page}&page_size=${pageSize}`;

    return publicInstance
        .get(`/v1/public/news?${query}`)
        .then((res: AxiosResponse<PaginationResponse<Announcement[]>>) => res.data)
}

async function editAnnouncement(id: string, data: NewAnnouncementData) {
    return protectedInstance
        .post(`/news/${id}`, data)
        .then((res: AxiosResponse<Response<Announcement>>) => res.data)
}

async function deleteAnnouncement(id: string) {
    return protectedInstance
        .delete(`/news/${id}`, {})
        .then((res: AxiosResponse<Response<Announcement>>) => res.data)
}

async function getFoldersRoot(page: number = 0, pageSize: number = 10) {
    console.log(`Calling getFoldersRoot API with page=${page}, pageSize=${pageSize}`);
    return protectedInstance
        .get(`/folders/root?page=${page}&page_size=${pageSize}`)
        .then((res: AxiosResponse) => {
            console.log('Raw API response:', res.data);
            return res.data;
        })
        .catch(error => {
            console.error('API Error in getFoldersRoot:', error);
            throw error;
        })
}

// Интерфейс для API ответа с подпапками
interface FolderApiItem {
    id: number;
    name: string;
    type: 'folder' | 'file';
    path: string;
    folderCount?: number;
    fileCount?: number;
    size?: number;
    processedFileId?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface FolderApiResponse {
    data: FolderApiItem[];
    totalElements: number;
    totalPages: number;
}

interface ApiResponse {
    data: FolderApiResponse;
    code: string;
    status: string;
}

async function getSubFolders(folderId: number | string, page: number = 0, pageSize: number = 10): Promise<ApiResponse> {
    console.log(`Calling getSubFolders API for folder ID ${folderId} with page=${page}, pageSize=${pageSize}`);
    return protectedInstance
        .get(`/folders/${folderId}/subfolders?page=${page}&page_size=${pageSize}`)
        .then((res: AxiosResponse<ApiResponse>) => {
            console.log('Raw API response for subfolders:', res.data);
            return res.data;
        })
        .catch(error => {
            console.error('API Error in getSubFolders:', error);
            throw error;
        })
}

const loadImagePreview = async (fileId: number | string, derivative: string | null = null) => {
    try {
        return protectedInstance
            .get(`/files/${fileId}/preview` + (derivative ? `?derivative=${derivative}` : ''), {
                responseType: 'arraybuffer'
            });
    } catch (error) {
        console.error('Network error:', error);
        return null;
    }
};

export const adminService = {
    getCompanies,
    saveNewCompany,
    getUsers,
    saveNewAnnouncement,
    getAnnouncements,
    editAnnouncement,
    deleteAnnouncement,
    getFoldersRoot,
    getSubFolders,
    loadImagePreview
};
